// Raw API response types

export interface ISubscriptionDeviceRowRaw {
  device_id: number
  device_name: string | null
  location_id: number
  location_name: string
  billing_entity_id: number
  billing_entity_name: string
  stripe_subscription_id: string | null
  stripe_recurring_price_id: string | null
  subscription_status: string | null
  current_period_end: string | null
  trial_end: string | null
  /** Authoritative payment classification computed by the backend. */
  payment_state: DeviceAlertStatus | null
  paid_through: string | null
  paid_invoice_id: string | null
  override_active: boolean
  override_reason: string | null
  stripe_derived_status: string | null
  draft_invoice_id: string | null
  po_number: string | null
  po_mode: number
  monitoring_price_minor: number | null
  currency: string | null
}

export interface IInvoiceRowRaw {
  invoice_id: string
  billing_entity_id: number
  subscription_id: string | null
  number: string | null
  amount_due: number
  amount_paid: number
  currency: string
  status: string
  invoice_pdf: string | null
  hosted_invoice_url: string | null
  period_start: string | null
  period_end: string | null
  location_name: string | null
  device_name: string | null
  requires_po: boolean
  po_number: string | null
}

/** Body for POST /subscriptions/status_override */
export interface SetSubscriptionOverrideData {
  device_account_link_id: number
  status: DeviceAlertStatus
  /** Required: why this device is being overridden, for the audit trail. */
  reason: string
}

/** Response from the status-override endpoints. */
export interface ISubscriptionOverrideRow {
  id: number
  device_account_link_id: number
  status: string
  reason: string
  created_by_email: string | null
  created_at: string
  previous_effective_status: string | null
  previous_stripe_status: string | null
  cleared_at: string | null
  cleared_reason: string | null
}

/** Body for POST /subscriptions/invoices/finalize */
export interface FinalizeInvoiceData {
  invoice_id: string
  /** Omitted for entities that don't require an individual PO per invoice. */
  po_number?: string
}

// Transformed frontend types

export interface ISubscriptionDeviceRow {
  deviceId: number
  deviceName: string | null
  locationId: number
  locationName: string
  billingEntityId: number
  billingEntityName: string
  stripeSubscriptionId: string | null
  stripeRecurringPriceId: string | null
  subscriptionStatus: string | null
  currentPeriodEnd: string | null
  trialEnd: string | null
  /**
   * The state every screen must render. Computed server-side so Devices &
   * Payments, Invoice History and the Dashboard cannot disagree (SF-APP-007).
   */
  paymentState: DeviceAlertStatus | null
  /** How far paid invoices cover this device; may exceed currentPeriodEnd. */
  paidThrough: string | null
  paidInvoiceId: string | null
  /** True while an admin override masks the Stripe-derived status. */
  overrideActive: boolean
  overrideReason: string | null
  /** The Stripe status the override is masking, shown alongside it. */
  stripeDerivedStatus: string | null
  /** Held draft invoice awaiting a PO (SF-APP-010). */
  draftInvoiceId: string | null
  poNumber: string | null
  /** 0 = no PO, 1 = one constant PO, 2 = a separate PO per invoice. */
  poMode: number
  /** Recurring price in minor units, for dashboard monetary totals. */
  monitoringPriceMinor: number | null
  currency: string | null
}

export interface IInvoiceRow {
  invoiceId: string
  billingEntityId: number
  /** Null for one-off / quote-backed invoices, which belong to no device. */
  subscriptionId: string | null
  number: string | null
  amountDue: number
  amountPaid: number
  currency: string
  status: string
  invoicePdf: string | null
  hostedInvoiceUrl: string | null
  periodStart: string | null
  periodEnd: string | null
  locationName: string | null
  deviceName: string | null
  /** Billing entity requires an individual PO for every invoice. */
  requiresPo: boolean
  poNumber: string | null
}

// Derived alert status for the Devices & Payments UI.
// Order of precedence matches the alert banners on the page.
export type DeviceAlertStatus =
  | 'paid'
  | 'restricted'
  | 'overdue'
  | 'upcoming'
  | 'pending'
  | 'healthy'
  | 'no_subscription'

const MS_PER_DAY = 1000 * 60 * 60 * 24

/** Renewals within this many days count as upcoming rather than healthy. */
const UPCOMING_WINDOW_DAYS = 30

/**
 * Classify a device for display.
 *
 * Prefers the backend's `paymentState`, which is the single authority across
 * every screen and the only thing that knows about paid future coverage and
 * admin overrides. The local derivation below is a fallback for rows served by
 * an older API build; it cannot detect 'paid' because that depends on invoice
 * data the device row alone does not carry.
 */
export function deriveDeviceStatus(row: ISubscriptionDeviceRow): DeviceAlertStatus {
  if (row.paymentState) return row.paymentState

  const s = row.subscriptionStatus
  if (!row.stripeSubscriptionId) return 'no_subscription'
  if (s === 'canceled' || s === 'unpaid') return 'restricted'
  if (s === 'past_due') return 'overdue'
  if (s === 'trialing' || s === null) return 'pending'
  if (s === 'active' && row.currentPeriodEnd) {
    const daysLeft = (new Date(row.currentPeriodEnd).getTime() - Date.now()) / MS_PER_DAY
    if (daysLeft <= 0) return 'overdue'
    if (daysLeft <= UPCOMING_WINDOW_DAYS) return 'upcoming'
    return 'healthy'
  }
  if (s === 'active') return 'healthy'
  return 'upcoming'
}

/** True when paid coverage runs past the active Stripe period. */
export function isPaidAhead(row: ISubscriptionDeviceRow): boolean {
  if (!row.paidThrough || !row.currentPeriodEnd) return false
  return new Date(row.paidThrough).getTime() > new Date(row.currentPeriodEnd).getTime()
}

/** Devices whose next term is already settled must not count as upcoming. */
export function countsAsUpcoming(status: DeviceAlertStatus): boolean {
  return status === 'upcoming'
}
